import { useQuery } from '@tanstack/react-query';
import { publicPost } from 'helpers/requests';
import { useEexAnnualPrices } from 'modules/Analytics/hooks/useEexAnnualPrices';
import { buildPublicPortalPath } from '../publicPortalRoutes';

/**
 * EEX quarterly certificate reference prices for the portal's cost-assumption
 * strip.
 *
 * Two entry points, because the strip renders on two pages with different
 * identities — and the price a visitor is shown must not depend on which:
 *
 *   usePortalCertificatePrices()   the PUBLIC share link. Anonymous visitor, so
 *                                  the gated portal endpoint, password and all.
 *   useOperatorCertificatePrices() the operator's own Studio preview. Already
 *                                  signed in, and holds only the HASH of their
 *                                  portal password — it cannot re-present the
 *                                  plaintext to the public gate, so it reads the
 *                                  authenticated endpoint instead.
 *
 * Both resolve to the same records through the same backend module, so the two
 * views agree by construction rather than by anyone remembering to keep them
 * aligned.
 *
 * Shape returned by both: `{ quarters, isLoading }`, where each quarter is
 * `{ quarter: 'Q1', status: 'confirmed' | 'projected', averagePrice, year }`.
 * Quarters with no price are absent — not zero, not null.
 */

const EMPTY = [];

const fetchPortalPrices = async (slug, password, surface) => {
  const res = await publicPost(`${buildPublicPortalPath(slug, { surface })}/certificate-prices`, {
    password: password ?? null,
  });
  return res.data ?? {};
};

export function usePortalCertificatePrices({ slug, password, surface = 'page', enabled = true }) {
  const query = useQuery({
    // The password is part of the key: clearing the gate is what makes the call
    // answerable, so a cached 401 must not be reused once it is supplied.
    queryKey: ['portal-certificate-prices', slug, surface, password ?? null],
    queryFn: () => fetchPortalPrices(slug, password, surface),
    enabled: Boolean(slug) && enabled,
    // Quarter averages move once a week at most. An hour of staleness costs the
    // viewer nothing and keeps a shared link from re-fetching on every mount.
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    // A failed suggestion list is not worth a retry storm on a public page: the
    // control still works, the viewer just types their own number.
    retry: false,
  });

  return {
    quarters: query.data?.quarters ?? EMPTY,
    isLoading: query.isLoading,
  };
}

export function useOperatorCertificatePrices({ enabled = true } = {}) {
  const year = new Date().getFullYear();
  const { data, isLoading } = useEexAnnualPrices({ year, enabled });

  const statuses = data?.quarterPriceStatuses ?? null;
  const quarters = statuses
    ? ['Q1', 'Q2', 'Q3', 'Q4']
        // `confirmed` only, to match the portal exactly. /eex/price/annual also
        // returns a `projected` current quarter, derived from the weekly closes
        // — a seed for the editable input on the Analytics page, not a published
        // average, and not something the portal publishes. This page's own
        // subtitle promises "exactly what downstream partners see on your share
        // link", so showing a suggestion they will never see would break it.
        .filter((q) => statuses[q]?.averagePrice != null && statuses[q]?.status === 'confirmed')
        .map((q) => ({
          quarter: q,
          status: statuses[q].status,
          averagePrice: statuses[q].averagePrice,
          year: statuses[q].year,
        }))
    : EMPTY;

  return { quarters, isLoading };
}
